// MyRoutes.jsx — Mart 01, 2026
// SUBCO (ve isteyen EMPLOYEE'ler) kendisine atanmış routes/planlamaları
// listeler. Backend: GET /api/routes/mine (driver_id == current_user.id)
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, Truck, Calendar, MapPin, Clock } from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function MyRoutes() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [window, setWindow] = useState(null);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/routes/mine?days_back=30&days_ahead=60');
      setRoutes(r.data?.data || []);
      setWindow(r.data?.window || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Routes yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const today = window?.today || new Date().toISOString().slice(0, 10);
  const todayRoutes = routes.filter((r) => r.date === today);
  const upcomingRoutes = routes.filter((r) => r.date > today).sort((a, b) => a.date.localeCompare(b.date));
  const pastRoutes = routes.filter((r) => r.date < today);

  return (
    <div className="space-y-6 p-4 max-w-5xl mx-auto" data-testid="my-routes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6 text-indigo-600" />
            Planlamalarım
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Hoşgeldin{user?.full_name ? `, ${user.full_name}` : ''}. Sana atanmış routeler aşağıda.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRoutes} disabled={loading} data-testid="my-routes-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Yenile
        </Button>
      </div>

      {loading && routes.length === 0 && (
        <Card><CardContent className="py-10 text-center text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Yükleniyor...
        </CardContent></Card>
      )}

      {!loading && routes.length === 0 && (
        <Card><CardContent className="py-16 text-center text-gray-500">
          <Truck className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">Henüz atanmış bir planlaman yok</p>
          <p className="text-xs mt-1">Admin seni bir route'a atadığında burada görünecek.</p>
        </CardContent></Card>
      )}

      {todayRoutes.length > 0 && (
        <RouteSection title="📍 Bugün" routes={todayRoutes} highlighted />
      )}
      {upcomingRoutes.length > 0 && (
        <RouteSection title="📅 Yaklaşan" routes={upcomingRoutes} />
      )}
      {pastRoutes.length > 0 && (
        <RouteSection title="🗂️ Geçmiş (Son 30 gün)" routes={pastRoutes} muted />
      )}
    </div>
  );
}

function RouteSection({ title, routes, highlighted, muted }) {
  return (
    <Card className={highlighted ? 'border-indigo-200 bg-indigo-50/30' : muted ? 'opacity-80' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="secondary">{routes.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {routes.map((r) => <RouteCard key={r.id} route={r} />)}
      </CardContent>
    </Card>
  );
}

function RouteCard({ route }) {
  const date = route.date || route.route_date;
  const dateObj = date ? new Date(date) : null;
  const dateLabel = dateObj
    ? dateObj.toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'short' })
    : date;
  return (
    <div className="border rounded-lg p-3 bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-indigo-100 text-indigo-700 border-0 font-mono text-xs">
              {route.customer?.toUpperCase() || 'ROUTE'}
            </Badge>
            {route.route_number && (
              <span className="font-semibold text-sm">#{route.route_number}</span>
            )}
            {route.license_plate && (
              <Badge variant="outline" className="font-mono text-xs">
                <Truck className="h-3 w-3 mr-1" />{route.license_plate}
              </Badge>
            )}
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs text-gray-600 flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{dateLabel}</span>
            {(route.start_time || route.end_time) && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {route.start_time || '--'} → {route.end_time || '--'}
              </span>
            )}
            {route.plaats && (
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{route.plaats}</span>
            )}
          </div>
          {route.notes && (
            <p className="mt-2 text-xs text-gray-500 italic line-clamp-2">💬 {route.notes}</p>
          )}
        </div>
        <div className="text-right">
          {route.stops != null && (
            <div className="text-xs text-gray-500">Stops</div>
          )}
          {route.stops != null && (
            <div className="text-lg font-bold text-indigo-600">{route.stops}</div>
          )}
        </div>
      </div>
    </div>
  );
}
